import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import { standFor, team, accreditations } from "@/data/site";

export default function About() {
  return (
    <>
      <Seo
        title="About | Incremental Consulting"
        description="Incremental Consulting is a 100% Black-owned advisory firm that delivers complex, regulated change for South African financial institutions — and independently reviews it when a board needs certainty."
      />
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227]" data-testid="about-eyebrow">About Us</p>
            <h1 className="mt-3 max-w-3xl text-balance font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="about-hero-title">
              A boutique built where delivery meets financial governance.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70">
              Incremental Consulting is a 100% Black-owned advisory firm that delivers complex, regulated change for South African financial institutions — and independently reviews it when a board needs certainty.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 lg:grid-cols-5 lg:px-8 lg:py-28">
          <div className="lg:col-span-3">
            <SectionHeading eyebrow="Who We Are" title="Practitioners, not career consultants." testId="about-who" />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#172033]/85" data-testid="about-story">
                <p>We founded Incremental Consulting to do one thing deeply: help organisations deliver complex, regulated change with confidence, and prove that it will land.</p>
                <p>Our edge is the pairing at the top of the firm — a founder with a decade of financial-services programme delivery, and a Chartered Accountant director. That lets us work where delivery discipline and financial governance meet, the exact point where large regulated programmes tend to succeed or fail.</p>
                <p>We are already delivering, across banking, asset management and telecommunications. And because we are independent of the delivery houses, when we review rather than deliver, the verdict a board gets can be trusted.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="rounded-xl bg-[#0B1F3A] p-8" data-testid="about-badges">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227]">Credentials</p>
              <ul className="mt-5 space-y-3.5">
                {accreditations.map((a) => (
                  <li key={a} className="flex items-center gap-2.5 text-sm font-semibold text-white/85">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Leadership"
            title="The people behind the work."
            sub="Delivery leadership and financial governance, under one roof."
            testId="about-leadership"
          />
          <div className="mx-auto mt-12 grid max-w-4xl gap-8 lg:grid-cols-2">
            {team.map((m, i) => (
              <Reveal key={m.name + m.role} delay={i * 0.12}>
                <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-[#0B1F3A]/10 bg-white shadow-[0_16px_40px_-28px_rgba(11,31,58,0.4)]" data-testid={`team-card-${m.role.toLowerCase()}`}>
                  {m.photo ? (
                    <div className="relative overflow-hidden">
                      <img
                        src={m.photo}
                        alt={`Portrait of ${m.name}, ${m.role} of Incremental Consulting`}
                        className="aspect-[5/4] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                        data-testid={`team-photo-${m.role.toLowerCase()}`}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B1F3A]/85 to-transparent px-7 pb-5 pt-16">
                        <h3 className="font-display text-2xl font-extrabold text-white">{m.name}</h3>
                        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#C9A227]">{m.role}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex aspect-[5/4] w-full items-center justify-center bg-[#0B1F3A]" aria-label={`Photo of ${m.name} to be added`}>
                      <span className="font-display text-5xl font-extrabold text-[#C9A227]">{m.initials}</span>
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-7">
                    <p className="flex-1 text-sm leading-relaxed text-[#172033]/85">{m.bio}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {m.certs.map((c) => (
                        <span key={c} className="rounded-full bg-[#0B1F3A]/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0B1F3A]/70">{c}</span>
                      ))}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading eyebrow="What We Stand For" title="How we show up." testId="about-values" />
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {standFor.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="block h-0.5 w-10 bg-[#C9A227]" />
                  <h3 className="mt-4 font-display text-lg font-bold text-[#0B1F3A]">{v.title}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[#667085]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <Link to="/services" data-testid="about-services-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">
              See our services
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand title="Let's talk about your programme." text="Tell us what you need to land. We'll show you how we'd deliver it — or review whether it's on track." />
    </>
  );
}
